import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback, Form, Table } from 'reactstrap';
import StatusCode from '../../../../../../util/StatusCode';
import CheckboxListItem from '../../../../../misc/CheckboxListItem';
import EnumInput from '../../../../../misc/EnumInput';
import { getSystemNames } from '../../../../../../store/ModuleAction';
import { RequestState } from '../../../../../../models/RequestState';
import { RootState } from '../../../../../../store/store';
import { CoreGoogleClassroomConfiguration } from '../../../../../../models/CoreGoogleClassroomModels';
import { GoogleClassroomMemberModeOptions } from './GoogleClassroomExportTypes';
import InformationMarkingsInput from '../InformationMarkingsInput';
import NullableIntInputCheckboxText from '../../../../../misc/NullableIntInputCheckboxText';
import i18n from 'i18next';
import StringValueBuilderInput, { StringValueBuilderFunctions } from '../StringValueBuilderInput';
import { CoreStringValueBuilderPart, StringValueBuilderPartValueSource } from '../../../../../../models/CoreModels';
import {
    defaultStringValueBuilderPartDefinitions,
    pickStringValueBuilderDefinitions
} from '../StringValueBuilderDefinitions';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ModalComponent from '../../../../../misc/ModalComponent';


export const classroomActivityStringValueBuilderPartDefinitions = pickStringValueBuilderDefinitions(
    defaultStringValueBuilderPartDefinitions,
    [
        StringValueBuilderPartValueSource.ACTIVITYNAME,
        StringValueBuilderPartValueSource.GROUPNAME,
        StringValueBuilderPartValueSource.SUBJECTCODE,
        StringValueBuilderPartValueSource.SCHOOLNAME,
        StringValueBuilderPartValueSource.SCHOOLNAME_SHORT,
        StringValueBuilderPartValueSource.SCHOOLNAME_SHORT_FALLBACK_SCHOOLNAME,
        StringValueBuilderPartValueSource.CONSTANT
    ]
);

export const classroomClassesStringValueBuilderPartDefinitions = pickStringValueBuilderDefinitions(
    defaultStringValueBuilderPartDefinitions,
    [
        StringValueBuilderPartValueSource.GROUPNAME,
        StringValueBuilderPartValueSource.SCHOOLNAME,
        StringValueBuilderPartValueSource.SCHOOLNAME_SHORT,
        StringValueBuilderPartValueSource.SCHOOLNAME_SHORT_FALLBACK_SCHOOLNAME,
        StringValueBuilderPartValueSource.CONSTANT
    ]
);

export const validator = (configuration: CoreGoogleClassroomConfiguration) => {
    const validation: string[] = [];

    if (configuration.archiveGracePeriod !== null && configuration.archiveGracePeriod < 0) {
        validation.push('archiveGracePeriod')
    }
    if (configuration.deleteGracePeriod === undefined || (configuration.deleteGracePeriod !== null && configuration.deleteGracePeriod < 0)) {
        validation.push('deleteGracePeriod');
    }
    if (configuration.includeClasses && !configuration.classroomClassesNaming.length) {
        validation.push('classroomClassesNaming');
    }
    if (configuration.includeCourses && !configuration.classroomCoursesNaming.length) {
        validation.push('classroomCoursesNaming');
    }

    return validation;
}

export const warningMessages = (originalConfiguration: CoreGoogleClassroomConfiguration, updatedConfiguration: CoreGoogleClassroomConfiguration) => {
    const warnings: string[] = [];

    if (updatedConfiguration.deleteGracePeriod === 0) {
        warnings.push(i18n.t('googleClassroomExport.lifecycleManagement.deleteGracePeriod.warning', { ns: 'configuration' }))
    }

    return warnings;
}

interface GoogleClassroomExportConfigurationProps {
    disabled: boolean
    configuration: CoreGoogleClassroomConfiguration
    updatePart: (part: Partial<CoreGoogleClassroomConfiguration>) => void
    onValidChange: (arg: boolean) => void
}

type NamingType = 'classroomClassesNaming' | 'classroomCoursesNaming';

const GoogleClassroomExportConfiguration = (props: GoogleClassroomExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const dispatch = useDispatch();

    const [open, setOpen] = useState<boolean>(true);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [namingType, setNamingType] = useState<NamingType | null>(null);
    const [aliasTrackingModuleTypeChecked, setAliasTrackningModuleTypeChecked] = useState<boolean>(props.configuration.aliasTrackingModuleType ? true : false)

    const systemNames = useSelector<RootState, RequestState<string[]>>(state => state.module.systemNames);

    const validation = validator(props.configuration);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration]);

    useEffect(() => {
        getSystemNames()(dispatch);
    }, [dispatch]);

    const addServiceAccount = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        let serviceAccounts = [...props.configuration.serviceAccounts];
        const newServiceAccount = e.currentTarget.newServiceAccount.value;

        if (serviceAccounts && serviceAccounts.some(sa => sa.emailAddress === newServiceAccount)) {
            return;
        } else {
            serviceAccounts.push({ emailAddress: newServiceAccount, classroomOwnerCount: 0 })
        }
        e.currentTarget.newServiceAccount.value = "";
        props.updatePart({ serviceAccounts: serviceAccounts });
    }

    const removeServiceAccount = (index: number) => {
        const updatedServiceAccounts = [...props.configuration.serviceAccounts];
        updatedServiceAccounts.splice(index, 1);
        props.updatePart({ serviceAccounts: updatedServiceAccounts });
    }

    const updateAliasTrackingModuleType = (prop: string, value: boolean | string) => {
        if (prop === 'aliasTrackingModuleTypeChecked') {
            setAliasTrackningModuleTypeChecked(Boolean(value));
        }
        if (prop === 'aliasTrackingModuleTypeChecked' && value === false) {
            props.updatePart({ aliasTrackingModuleType: null })
        }
        if (prop === 'aliasTrackingModuleType') {
            props.updatePart({ aliasTrackingModuleType: value.toString() })
        }
    }

    const renderNaming = () => {
        const namingParts: Record<NamingType, CoreStringValueBuilderPart[]> = {
            classroomClassesNaming: props.configuration.classroomClassesNaming ?? [],
            classroomCoursesNaming: props.configuration.classroomCoursesNaming ?? []
        };

        const namingPartDefinitions = {
            classroomClassesNaming: classroomClassesStringValueBuilderPartDefinitions,
            classroomCoursesNaming: classroomActivityStringValueBuilderPartDefinitions
        };

        const parts: CoreStringValueBuilderPart[] = namingType ? namingParts[namingType] : [];
        const selectableParts = namingType ? namingPartDefinitions[namingType] : null;

        if (openModal && selectableParts && namingType) {
            return (
                <ModalComponent
                    isOpen={true}
                    toggleModal={() => (setOpenModal(false), setNamingType(null))}
                    header={t(`googleClassroomExport.naming.header.${namingType}`, { ns: 'configuration'})}
                    cancel={t('action.close', { ns: 'common' })}
                    size="lg"
                >
                    <StringValueBuilderInput
                        name={namingType}
                        parts={parts}
                        onChange={(name, value) => props.updatePart({ [name]: value })}
                        disabled={props.disabled}
                        partDefinitions={selectableParts}
                        largeInput={true}
                    />
                </ModalComponent>
            )
        }
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.GoogleClassroomExport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <FormGroup>
                            <h5>{t('googleClassroomExport.type.header', { ns: 'configuration' })}</h5>
                            <CheckboxListItem
                                text={t('googleClassroomExport.type.class', { ns: 'configuration' })}
                                id='includeClasses'
                                name='includeClasses'
                                value={props.configuration.includeClasses}
                                onChange={(e) => props.updatePart({ includeClasses: e.target.checked })}
                                disabled={props.disabled}
                            />
                            <CheckboxListItem
                                text={t('googleClassroomExport.type.course', { ns: 'configuration' })}
                                id='includeCourses'
                                name='includeCourses'
                                value={props.configuration.includeCourses}
                                onChange={(e) => props.updatePart({ includeCourses: e.target.checked })}
                                disabled={props.disabled}
                            />
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('googleClassroomExport.naming.title', { ns: 'configuration' })}</h5>
                            <FormGroup>
                                <div className="flex align-items-center">
                                    <span>{t('googleClassroomExport.type.class', { ns: 'configuration' })}: {' '}
                                        {props.configuration.classroomClassesNaming.map(x => `[${StringValueBuilderFunctions.renderItem(t, x)}]`).join(", ")}
                                        {props.configuration.classroomClassesNaming.length ?
                                            <span><i>{' '}({t('googleClassroomExport.naming.example', { ns: 'configuration' })}: {StringValueBuilderFunctions.renderExample(t, props.configuration.classroomClassesNaming)})</i></span> : null}
                                    </span>
                                    <FontAwesomeIcon className="ms-3" icon="pen" onClick={() => (setOpenModal(true), setNamingType('classroomClassesNaming'))} />
                                </div>
                                {validation.indexOf('classroomClassesNaming') !== -1 &&
                                    <FormFeedback className="d-block">{t('googleClassroomExport.naming.invalidClasses', { ns: 'configuration' })}</FormFeedback>}

                            </FormGroup>
                            <div className="flex align-items-center">
                                <span>{t('googleClassroomExport.type.course', { ns: 'configuration' })}: {' '}
                                    {props.configuration.classroomCoursesNaming.map(x => `[${StringValueBuilderFunctions.renderItem(t, x)}]`).join(", ")}
                                    {props.configuration.classroomCoursesNaming.length ?
                                        <span><i> {' '}({t('googleClassroomExport.naming.example', { ns: 'configuration' })}: {StringValueBuilderFunctions.renderExample(t, props.configuration.classroomCoursesNaming)})</i></span> : null}
                                </span>
                                <FontAwesomeIcon className="ms-3" icon="pen" onClick={() => (setOpenModal(true), setNamingType('classroomCoursesNaming'))} />
                            </div>
                            {validation.indexOf('classroomCoursesNaming') !== -1 &&
                                <FormFeedback className="d-block">{t('googleClassroomExport.naming.invalidCourses', { ns: 'configuration' })}</FormFeedback>}

                        </FormGroup>

                        {renderNaming()}

                        <InformationMarkingsInput
                            selectedValues={props.configuration.activityInformationMarkingFilters}
                            name='activityInformationMarkingFilters'
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                        />

                        <EnumInput
                            title={t('googleClassroomExport.memberMode.student', { ns: 'configuration' })}
                            name='studentMemberMode'
                            value={props.configuration.studentMemberMode}
                            options={GoogleClassroomMemberModeOptions.map((keyvalue) => ({ value: keyvalue, text: t(`googleClassroomExport.memberMode.options.${keyvalue}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <EnumInput
                            title={t('googleClassroomExport.memberMode.teacher', { ns: 'configuration' })}
                            name='teacherMemberMode'
                            value={props.configuration.teacherMemberMode}
                            options={GoogleClassroomMemberModeOptions.map((keyvalue) => ({ value: keyvalue, text: t(`googleClassroomExport.memberMode.options.${keyvalue}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <FormGroup>
                            <h5>{t('googleClassroomExport.lifecycleManagement.header', { ns: 'configuration' })}</h5>
                            <FormGroup>
                                <Label for="archiveGracePeriod"> {t('googleClassroomExport.lifecycleManagement.archiveGraceperiod.label', { ns: 'configuration' })}</Label>
                                <Input
                                    className="md-w-50 lg-w-25"
                                    type="number"
                                    name="archiveGracePeriod"
                                    id="archiveGracePeriod"
                                    min="0"
                                    step="1"
                                    pattern="[0-9]"
                                    disabled={props.disabled}
                                    value={props.configuration.archiveGracePeriod === null ? '' : props.configuration.archiveGracePeriod}
                                    onChange={(e) => props.updatePart({ archiveGracePeriod: Number(e.target.value) })}
                                    invalid={validation.indexOf('archiveGracePeriod') !== -1} />
                                <FormFeedback>{t('googleClassroomExport.lifecycleManagement.archiveGraceperiod.formFeedback', { ns: 'configuration' })}</FormFeedback>
                            </FormGroup>

                            <NullableIntInputCheckboxText
                                name="deleteGracePeriod"
                                value={props.configuration.deleteGracePeriod}
                                disabled={props.disabled}
                                title={t('googleClassroomExport.lifecycleManagement.deleteGracePeriod.title', { ns: 'configuration' })}
                                invalidFeedback={t('googleClassroomExport.lifecycleManagement.deleteGracePeriod.invalidFeedback', { ns: 'configuration' })}
                                invalid={validation.indexOf('deleteGracePeriod') !== -1}
                                defaultValue={365}
                                onChange={(name, value) => props.updatePart({ deleteGracePeriod: value })}
                                checkboxTitle={t('googleClassroomExport.lifecycleManagement.deleteGracePeriod.checkbox', { ns: 'configuration' })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <h5>{t('googleClassroomExport.serviceAccounts.header', { ns: 'configuration' })}</h5>
                            <Form onSubmit={(e) => addServiceAccount(e)}>
                                <Table striped>
                                    <thead>
                                        <tr>
                                            <th>{t('googleClassroomExport.serviceAccounts.tableHeaders.email', { ns: 'configuration' })}</th>
                                            <th>{t('googleClassroomExport.serviceAccounts.tableHeaders.id', { ns: 'configuration' })}</th>
                                            <th className='text-right'>{t('googleClassroomExport.serviceAccounts.tableHeaders.classroomCount', { ns: 'configuration' })}</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {props.configuration.serviceAccounts && props.configuration.serviceAccounts.map((acc, index) => (
                                            <tr key={acc.id ? acc.id : acc.emailAddress}>
                                                <td>{acc.emailAddress}</td>
                                                <td>{acc.googleId}</td>
                                                <td className='text-right'>{acc.classroomOwnerCount}</td>
                                                <td className='text-right'>
                                                    <Button
                                                        color="danger"
                                                        onClick={() => removeServiceAccount(index)}
                                                        disabled={props.disabled || acc.classroomOwnerCount > 0}>
                                                        {t('googleClassroomExport.serviceAccounts.button.remove', { ns: 'configuration' })}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td colSpan={3}>
                                                <Input
                                                    type="email"
                                                    name="newServiceAccount"
                                                    disabled={props.disabled}
                                                    required
                                                    placeholder={t('googleClassroomExport.serviceAccounts.placeholder.email', { ns: 'configuration' })}
                                                />
                                            </td>
                                            <td className='text-right'>
                                                <Button
                                                    color="primary"
                                                    type="submit"
                                                    disabled={props.disabled}>{t('action.add', { ns: 'common' })}
                                                </Button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Form>
                        </FormGroup>
                        <FormGroup>
                            <h5>{t('googleClassroomExport.aliasTrackingModule.header', { ns: 'configuration' })}</h5>
                            <FormGroup check>
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        id="aliasTrackingModuleTypeCheck"
                                        checked={aliasTrackingModuleTypeChecked}
                                        disabled={props.disabled}
                                        onChange={(e) => updateAliasTrackingModuleType('aliasTrackingModuleTypeChecked', e.target.checked)} />
                                    {t('googleClassroomExport.aliasTrackingModule.checkbox', { ns: 'configuration' })}
                                </Label>
                            </FormGroup>
                            <FormGroup>
                                <Label for="aliasTrackingModuleType">{t('googleClassroomExport.aliasTrackingModule.label', { ns: 'configuration' })}</Label>
                                <Input
                                    type="select"
                                    name="aliasTrackingModuleType"
                                    id="aliasTrackingModuleType"
                                    onChange={(e) => updateAliasTrackingModuleType('aliasTrackingModuleType', e.target.value)}
                                    disabled={!aliasTrackingModuleTypeChecked || props.disabled}
                                    value={props.configuration.aliasTrackingModuleType || ''}
                                    invalid={validation.indexOf('aliasTrackingModuleType') !== -1} >
                                    <option value='' disabled>{t('googleClassroomExport.aliasTrackingModule.optiontext', { ns: 'configuration' })}</option>
                                    {systemNames.code === StatusCode.COMPLETE && systemNames.data.map((type, i) => <option key={i} value={type}>{type}</option>)}
                                </Input>
                                <FormFeedback>{t('googleClassroomExport.aliasTrackingModule.formFeedback', { ns: 'configuration' })}</FormFeedback>
                            </FormGroup>
                        </FormGroup>
                    </div>
                </CardBody>
            </Collapse>
        </Card>)
}

export default GoogleClassroomExportConfiguration;
